import { JsonPatch } from "json8-patch";
import { flow, Instance, types, getEnv } from "mobx-state-tree";

import {
  DeleteSewerObject,
  DeleteSewerObjectsFromAssignedProject,
  EditSewerObject,
  EditSewerObjectFromAssignedProject,
  ViewSewerObject,
  ViewSewerObjectFromAssignedProject,
} from "../authorization/Permissions";
import { SectionDetailsResponse } from "../../api-models/response/SectionDetailsResponse";
import { currentUserService } from "../user/CurrentUserStorage";
import { emptySection, SectionDetails } from "./SectionDetails";
import { permissionsService } from "../authorization/UserPermissionsService";
import { Endpoint, endpoints } from "../api/ApiEndpoints";
import { StorageModel } from "../storage/StorageModel";
import { SewerObjectParentEntityFromPath } from "./SewerObjectParentEntityFromPath";
import { CurrentSewerObjectStorageModel } from "../storage/CurrentSewerObjectStorageModel";
import { compareAsc } from "date-fns";
import dateService from "../date-service/DateService";
import { InspectionDetails } from "../inspection/InspectionDetails";

const CurrentSectionModel = types.model({
  currentSectionId: types.frozen<number>(),
  parentEntityFromPath: types.frozen<SewerObjectParentEntityFromPath>(),
  currentSection: types.optional(types.frozen<SectionDetails>(), new SectionDetails(emptySection)),
  isDeleteInProgress: types.optional(types.boolean, false),
});

export const CurrentSectionStorageModel = types
  .compose(CurrentSectionModel, CurrentSewerObjectStorageModel, StorageModel)
  .named("CurrentSectionStorage")
  .actions((self) => {
    return {
      mapResponse(response: SectionDetailsResponse) {
        self.currentSection = new SectionDetails(response);
      },
      editSectionDetails: flow(function* editSectionDetails(sectionPatch: JsonPatch) {
        if (!sectionPatch.length) return;

        const endpoint = endpoints.get("section");
        endpoint.routeParams = [self.currentSectionId];

        return yield getEnv(self).api.patch(endpoint, sectionPatch);
      }),
      deleteSection: flow(function* deleteSection() {
        self.isDeleteInProgress = true;
        const endpoint = endpoints.get("section");
        endpoint.routeParams = [self.currentSectionId];

        const response = yield getEnv(self).api.delete(endpoint);
        self.isDeleteInProgress = false;
        return response;
      }),
      getOtherOutdatedInspectionsWithSameGatheringDate(inspectionDetails: InspectionDetails) {
        return self.currentSection.inspectionsWithDates.filter(
          (i) =>
            i.id !== inspectionDetails.id &&
            !i.isRelevant &&
            !i.isLocked &&
            i.gatheringDate &&
            inspectionDetails.gatheringDate &&
            compareAsc(
              dateService.getDateFromString(i.gatheringDate),
              dateService.getDateFromString(inspectionDetails.gatheringDate)
            ) === 0
        );
      },
    };
  })
  .views((self) => ({
    get canUserViewSection() {
      return (
        permissionsService.userHasPermissionTo(ViewSewerObject) ||
        permissionsService.userHasPermissionTo(ViewSewerObjectFromAssignedProject)
      );
    },
    get sectionIsLocked() {
      return !!self.currentSection.inspectionLockDetails;
    },
    get userWithLockOnSection() {
      return self.currentSection.inspectionLockDetails?.owner;
    },
    get currentUserHasLockOnSection() {
      return (
        self.currentSection.inspectionLockDetails?.owner.id === currentUserService.currentUser.id
      );
    },
  }))
  .views((self) => ({
    get canLoadItems() {
      return self.canUserViewSection && self.currentSectionId > 0;
    },
    get endpoint() {
      const endpoint: Endpoint = endpoints.get("section");
      endpoint.routeParams = [self.currentSectionId];
      return endpoint;
    },
    get isLoading() {
      return self.currentSection.id <= 0;
    },
    get canUserEditSection() {
      return (
        (!self.sectionIsLocked || self.currentUserHasLockOnSection) &&
        permissionsService.userHasOneOfThePermissions([
          EditSewerObject,
          EditSewerObjectFromAssignedProject,
        ])
      );
    },
    get canUserDeleteSection() {
      return (
        (!self.sectionIsLocked || self.currentUserHasLockOnSection) &&
        permissionsService.userHasOneOfThePermissions([
          DeleteSewerObject,
          DeleteSewerObjectsFromAssignedProject,
        ])
      );
    },
  }));

export type CurrentSectionStorage = Instance<typeof CurrentSectionStorageModel>;
